export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly sanitizedData?: unknown;
}

export interface ValidationOptions {
  readonly maxSize?: number;
  readonly allowedKeys?: string[];
  readonly sanitize?: boolean;
}

export interface SafeUpdateValidatorConfig {
  readonly maxSize: number;
  readonly maxDepth: number;
  readonly dangerousKeys: readonly string[];
  readonly sanitize: boolean;
}

const DEFAULT_CONFIG: SafeUpdateValidatorConfig = {
  maxSize: 1024 * 1024, // 1MB
  maxDepth: 10,
  dangerousKeys: ['__proto__', 'constructor', 'prototype'],
  sanitize: true,
};

export class SafeUpdateValidator {
  private readonly config: SafeUpdateValidatorConfig;

  constructor (config?: Partial<SafeUpdateValidatorConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  validate (data: unknown, options?: ValidationOptions): ValidationResult {
    const maxSize: number = options?.maxSize ?? this.config.maxSize;
    const shouldSanitize: boolean = options?.sanitize ?? this.config.sanitize;

    if (!this.isWithinSizeLimit(data, maxSize)) {
      return {
        valid: false,
        error: `Data exceeds maximum size of ${maxSize} bytes`,
      };
    }

    if (this.hasProtoProperties(data)) {
      return {
        valid: false,
        error: 'Data contains dangerous prototype properties',
      };
    }

    if (this.hasExcessiveDepth(data, this.config.maxDepth)) {
      return {
        valid: false,
        error: `Data exceeds maximum depth of ${this.config.maxDepth}`,
      };
    }

    if (options?.allowedKeys) {
      const validation: ValidationResult = this.validateAllowedKeys(data, options.allowedKeys);
      if (!validation.valid) {
        return validation;
      }
    }

    if (shouldSanitize) {
      const sanitizedData: unknown = this.sanitize(data);
      return {
        valid: true,
        sanitizedData,
      };
    }

    return { valid: true };
  }

  sanitize (data: unknown): unknown {
    if (data === null || data === undefined) {
      return data;
    }

    if (typeof data !== 'object') {
      return this.sanitizeScalar(data);
    }

    if (Array.isArray(data)) {
      return data.map(item => this.sanitize(item));
    }

    return this.sanitizeObject(data as Record<string, unknown>);
  }

  isWithinSizeLimit (data: unknown, maxSize: number): boolean {
    try {
      const serialized: string = JSON.stringify(data);
      return serialized.length <= maxSize;
    } catch {
      return false;
    }
  }

  hasProtoProperties (data: unknown, depth: number = 0): boolean {
    if (depth > this.config.maxDepth) {
      return false;
    }

    if (!data || typeof data !== 'object') {
      return false;
    }

    const obj: Record<string, unknown> = data as Record<string, unknown>;

    for (const key of this.config.dangerousKeys) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        return true;
      }
    }

    for (const value of Object.values(obj)) {
      if (this.hasProtoProperties(value, depth + 1)) {
        return true;
      }
    }

    return false;
  }

  private hasExcessiveDepth (data: unknown, maxDepth: number, currentDepth: number = 0): boolean {
    if (currentDepth > maxDepth) {
      return true;
    }

    if (!data || typeof data !== 'object') {
      return false;
    }

    if (Array.isArray(data)) {
      return data.some(item => this.hasExcessiveDepth(item, maxDepth, currentDepth + 1));
    }

    const obj: Record<string, unknown> = data as Record<string, unknown>;
    return Object.values(obj).some(value => this.hasExcessiveDepth(value, maxDepth, currentDepth + 1));
  }

  private validateAllowedKeys (data: unknown, allowedKeys: string[]): ValidationResult {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return { valid: true };
    }

    const obj: Record<string, unknown> = data as Record<string, unknown>;
    const keys: string[] = Object.keys(obj);
    const disallowedKeys: string[] = keys.filter(key => !allowedKeys.includes(key));

    if (disallowedKeys.length > 0) {
      return {
        valid: false,
        error: `Data contains disallowed keys: ${disallowedKeys.join(', ')}`,
      };
    }

    return { valid: true };
  }

  private sanitizeScalar (value: unknown): unknown {
    if (typeof value === 'string') {
      return value
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '');
    }

    return value;
  }

  private sanitizeObject (obj: Record<string, unknown>): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      if (this.config.dangerousKeys.includes(key)) {
        continue;
      }

      if (key.startsWith('_') && key.length > 1) {
        continue;
      }

      sanitized[key] = this.sanitize(value);
    }

    return sanitized;
  }
}
